import React, { useEffect, useState } from 'react';
import Layout from '../../../components/Layout/Layout';
import StatusBadge from '../../../components/StatusBadge/StatusBadge';
import api from '../../../api/axios';
import { downloadFile, fmtDate } from '../../../utils/download';

export default function Reports() {
  const [data, setData] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    api.get('/trainings').then(res => setTrainings(res.data));
    api.get('/certificates').then(res => setCertificates(res.data)).catch(() => {});
    fetchReports();
  }, []);

  async function fetchReports(trainingId = '') {
    setLoading(true);
    try {
      const res = await api.get('/reports', { params: trainingId ? { trainingId } : {} });
      setData(res.data);
    } finally { setLoading(false); }
  }

  function handleFilterChange(e) {
    setFilter(e.target.value);
    fetchReports(e.target.value);
  }

  function isOverdue(deadline, status, accessEndedAt) {
    if (!deadline || status === 'passed' || accessEndedAt) return false;
    return new Date(deadline) < new Date();
  }

  async function exportProgress(format) {
    setExporting(format);
    try {
      await downloadFile('/reports/export', `Kursfortschritt.${format}`, { format, ...(filter ? { trainingId: filter } : {}) });
    } catch {
      alert('Export fehlgeschlagen');
    } finally { setExporting(''); }
  }

  async function downloadCertificate(row) {
    try {
      if (row.certificateId) {
        await downloadFile(`/certificates/${row.certificateId}/download`, 'Teilnahmebestaetigung.pdf');
      } else {
        await downloadFile(`/user-trainings/${row.id}/certificate`, 'Zertifikat.pdf');
      }
    } catch {
      alert('Teilnahmebestätigung konnte nicht geladen werden');
    }
  }

  return (
    <Layout title="Berichte">
      <div style={{ marginBottom: '1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={filter} onChange={handleFilterChange} style={{ padding: '0.625rem 1rem', borderRadius: 8, border: '1px solid #e2e2e2', fontFamily: 'inherit', minWidth: 220 }}>
          <option value="">Alle Schulungen</option>
          {trainings.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" disabled={!!exporting} onClick={() => exportProgress('pdf')}>
          {exporting === 'pdf' ? 'Wird erstellt…' : 'Fortschritt als PDF'}
        </button>
        <button className="btn btn-ghost btn-sm" disabled={!!exporting} onClick={() => exportProgress('csv')}>
          {exporting === 'csv' ? 'Wird erstellt…' : 'Fortschritt als CSV'}
        </button>
      </div>

      {loading ? <div>Laden...</div> : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Mitarbeiter</th>
                <th>Schulung</th>
                <th>Status</th>
                <th>Begonnen</th>
                <th>Frist</th>
                <th>Abgeschlossen</th>
                <th>Versuche</th>
                <th>Letzte Note</th>
                <th>Bestätigung</th>
              </tr>
            </thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id} style={{ opacity: r.access_ended_at ? 0.6 : 1 }}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.userName}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-dark)' }}>{r.userEmail}</div>
                  </td>
                  <td>{r.trainingTitle}</td>
                  <td>
                    {r.access_ended_at
                      ? <StatusBadge status="ended" label={`Beendet ${fmtDate(r.access_ended_at)}`} />
                      : <StatusBadge status={r.status} />}
                  </td>
                  <td>{r.started_at ? fmtDate(r.started_at) : '–'}</td>
                  <td style={{ color: isOverdue(r.deadline, r.status, r.access_ended_at) ? 'var(--error)' : 'inherit', fontWeight: isOverdue(r.deadline, r.status, r.access_ended_at) ? 600 : 400 }}>
                    {r.deadline ? fmtDate(r.deadline) : '–'}
                    {isOverdue(r.deadline, r.status, r.access_ended_at) && <span style={{ marginLeft: '0.35rem', fontSize: '0.75rem' }}>überfällig</span>}
                  </td>
                  <td>{r.completed_at ? fmtDate(r.completed_at) : '–'}</td>
                  <td>{r.attemptCount}</td>
                  <td>{r.lastScore != null ? `${parseFloat(r.lastScore).toFixed(0)}%` : '–'}</td>
                  <td>
                    {(r.certificateId || r.status === 'passed') && (
                      <button className="btn btn-ghost btn-sm" onClick={() => downloadCertificate(r)}>PDF</button>
                    )}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--gray-dark)', padding: '2rem' }}>Keine Daten vorhanden</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' }}>Teilnahmebestätigungen</h2>
        <p style={{ fontSize: '0.8rem', color: 'var(--gray-dark)', marginBottom: '1rem' }}>
          Alle bestandenen Abschlusskontrollen, jederzeit als PDF exportierbar. Bestätigungen bleiben auch nach Übertragung
          einer Kurslizenz oder Löschung der Kursdaten erhalten und werden erst mit den Kontodaten gelöscht.
        </p>
        <div className="table-container">
          <table>
            <thead><tr><th>Teilnehmer</th><th>Schulung</th><th>Bestanden am</th><th>Ergebnis</th><th></th></tr></thead>
            <tbody>
              {certificates.map(c => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.user_name}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--gray-dark)' }}>{c.user_email}</div>
                  </td>
                  <td>{c.training_title}</td>
                  <td>{fmtDate(c.completed_at)}</td>
                  <td>{c.score != null ? `${parseFloat(c.score).toFixed(0)}%` : '–'}</td>
                  <td>
                    <button className="btn btn-ghost btn-sm" onClick={() => downloadFile(`/certificates/${c.id}/download`, 'Teilnahmebestaetigung.pdf').catch(() => alert('Download fehlgeschlagen'))}>
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
              {certificates.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--gray-dark)', padding: '1.5rem' }}>Noch keine Teilnahmebestätigungen</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
